import { User } from '../../Models/User';
import React, { CSSProperties, useEffect, useState } from 'react';
import { Table as BSTable } from 'flowbite-react';
import {
  ColumnDef,
  Row,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UserListRequest } from '../../Models/UserListRequest';
import { Link, useParams } from 'react-router-dom';
import MemberApi from '../../Api/MemberApi';
import { HiUser, HiXCircle } from 'react-icons/hi';
import { UserLevelNames } from '../../Models/UserLevel';
import { useAppSelector } from '../../Reducers/store';
import { toast } from 'react-toastify';
import UserApi from '../../Api/UserApi';
import { BsQrCode } from 'react-icons/bs';
import { Button } from 'flowbite-react';

const RowDragHandleCell = ({ rowId }: { rowId: string }) => {
  const { attributes, listeners } = useSortable({
    id: rowId,
  });
  return (
    <button {...attributes} {...listeners}>
      🟰
    </button>
  );
};

const DraggableRow = ({ row }: { row: Row<User> }) => {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: row.original.id.toString(),
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative',
  };
  return (
    <tr ref={setNodeRef} style={style}>
      {row.getVisibleCells().map((cell) => (
        <td key={cell.id} style={{ width: cell.column.getSize() }}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  );
};

export default function MemberContactListTable() {
  const { id } = useParams();
  const { userModel } = useAppSelector((state) => state.user);
  const [formData, setFormData] = useState<UserListRequest>({
    firstName: '',
    lastName: '',
    emailAddress: '',
  });
  const [userContactListData, setContactUserListData] = useState<User[]>([]);
  const [updateOrder, setupdateOrder] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    getUsers();
  }, [id]);

  const isSuperAdmin = userModel?.userLevelId === UserLevelNames.SuperAdmin;

  const getUsers = () => {
    if (id) {
      try {
        setIsLoading(true);
        return MemberApi.getMemberUserList(Number.parseInt(id), formData)
          .then((res) => {
            setContactUserListData(
              res.data.sort(function (a, b) {
                return (a.priority ?? 99) - (b.priority ?? 99);
              })
            );
            setIsLoading(false);
          })
          .catch((err) => {});
      } catch (error) {
        console.error(error);
        setIsLoading(false);
      }
    }
  };

  const downloadPdfNow = async (u: User) => {
    try {
      const response = await UserApi.getUserBadge(u.id);
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(pdfBlob);
      const tempLink = document.createElement('a');
      tempLink.href = url;
      tempLink.setAttribute(
        'download',
        `${u.firstName}_${u.lastName}_Badge.pdf`
      );

      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error downloading PDF');
    }
  };

  const handleDeleteUser = (user: User) => {
    if (!isSuperAdmin) return;
    const shouldRemove = window.confirm(
      'Are you sure you want to delete this User?'
    );
    if (!shouldRemove) return;

    return UserApi.deleteUser(user.id)
      .then((res) => {
        if (res.data.success) toast.success('User Deleted');
        else toast.error(res.data.message ?? 'Delete failed');
        getUsers();
      })
      .catch((err) => {
        toast.error('Error');
        console.error(err);
      });
  };

  const columns = React.useMemo<ColumnDef<User>[]>(
    () => [
      {
        accessorFn: (row) => row.id,
        id: 'id',
        cell: (info) => (
          <div className='flex justify-center'>
            <Link to={`/user/${info.getValue()}`} className='buttonMain p-2'>
              <HiUser></HiUser>
            </Link>
            {isSuperAdmin && (
              <button
                className='buttonMainBlue p-2 ml-2'
                onClick={() => downloadPdfNow(info.row.original)}
                title='Download badge PDF'
              >
                <BsQrCode />
              </button>
            )}
          </div>
        ),
        header: '',
        size: 35,
      },
      {
        accessorKey: 'firstName',
        cell: (info) => info.getValue(),
        header: () => <span>First Name</span>,
      },
      {
        accessorFn: (row) => row.lastName,
        id: 'lastName',
        cell: (info) => info.getValue(),
        header: () => <span>Last Name</span>,
      },
      {
        accessorKey: 'emailAddress',
        header: () => 'Email',
      },
      {
        accessorKey: 'phoneNumber',
        header: () => <span>Phone</span>,
      },
      {
        accessorKey: 'userLevel.name',
        header: () => <span>User Level</span>,
      },
      {
        id: 'drag-handle',
        header: 'Move',
        cell: ({ row }) => <RowDragHandleCell rowId={row.id} />,
        size: 30,
      },
      {
        id: 'delete',
        header: '',
        cell: (info) => (
          <div className='flex justify-center'>
            <Button
              className='buttonCancel p-2'
              onClick={() => handleDeleteUser(info.row.original)}
              size={20}
            >
              <HiXCircle />
            </Button>
          </div>
        ),
        size: 50,
      },
    ],
    [isSuperAdmin]
  );

  const dataIds = React.useMemo<UniqueIdentifier[]>(
    () => userContactListData?.map(({ id }) => id.toString()),
    [userContactListData]
  );

  useEffect(() => {
    if (updateOrder && dataIds.length > 0) {
      updateOrderList();
      setupdateOrder(false);
    }
  }, [updateOrder]);

  function checkAccess() {
    if (userModel && id) {
      if (userModel.userLevelId === UserLevelNames.SuperAdmin) {
        return true;
      } else if (
        userModel.memberId === Number(id) &&
        userModel.userLevelId === UserLevelNames.MemberAdmin
      ) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  const table = useReactTable({
    data: userContactListData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
    initialState: {
      columnVisibility: {
        'drag-handle': checkAccess(),
        delete: isSuperAdmin,
      },
    },
  });

  const updateOrderList = async () => {
    if (id && dataIds) {
      await MemberApi.updateContactOrder(+id, dataIds)
        .then((res) => {
          if (!res.data.success) {
            toast.error('Error: ' + res.data.message);
          }
        })
        .catch((err) => {
          toast.error('Tags Error: ' + err);
        });
    }
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setContactUserListData((data) => {
        const oldIndex = dataIds.indexOf(active.id);
        const newIndex = dataIds.indexOf(over.id);
        return arrayMove(data, oldIndex, newIndex); //this is just a splice util
      });
      setupdateOrder(true);
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <div className='w-full shadow-none pb-5'>
        <BSTable className=''>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            <SortableContext
              items={dataIds}
              strategy={verticalListSortingStrategy}
            >
              {table.getRowModel().rows.map((row) => (
                <DraggableRow key={row.id} row={row} />
              ))}
            </SortableContext>
          </tbody>
        </BSTable>
      </div>
    </DndContext>
  );
}
